// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { RootState } from 'admin/core/store';
import ComputeResourceTable from 'admin/computeResource/list/containers/ComputeResourceTable/ComputeResourceTable';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import * as computeResourceActions from 'admin/computeResource/actions';
import { PageHeader } from 'admin/common/components/PageHeader/PageHeader';
import InfiniteScroll from 'common/components/InfinityScroll/InfinityScroll';
import { StyledTable } from 'common/components/styles/StyledTable';
import { Translate } from '@plesk/ui-library';
import { Loader } from 'common/components';
import { hasPermission } from 'common/modules/permission/selectors';
import { PERMISSION_LIST } from 'common/modules/permission/constants';
import { useComputeResourceDialogs } from 'admin/computeResource/hooks/useComputeResourceDialogs';
import { useIsFirstLoading } from 'common/hooks/useIsFirstLoading';

export type ComputeResourcesProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const ComputeResources: React.FC<ComputeResourcesProps> = ({
    canGetComputeResources,
    loadingFlags: {
        isLoadingList,
    },
    list,
    computeResourceActions: {
        loadComputeResourcesOnScroll,
        getComputeResources,
    },
}) => {
    React.useEffect(() => {
        getComputeResources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const isFirstLoading = useIsFirstLoading(isLoadingList);

    const {
        handleCreate,
        handleEdit,
        handleSettings,
        handleDetailsClick,
        handleRemove,
        dialogs,
    } = useComputeResourceDialogs({
        items: list.data,
        syncSelectionToStore: true,
    });

    if (!canGetComputeResources) {
        return <Translate content="accessDenied" />;
    }

    return (
        <>
            <PageHeader
                isButtonShown={list.data.length > 0}
                title={<Translate content="computeResource.title"/>}
                buttonText="computeResource.addBtn"
                buttonIcon="card-row"
                onButtonClick={handleCreate}
            />
            <StyledTable verticalAlign="top">
                <InfiniteScroll
                    loadMore={loadComputeResourcesOnScroll}
                    hasMore={!!list.links.next}
                >
                    <Loader isLoading={isFirstLoading}>
                        <ComputeResourceTable
                            items={list.data}
                            onCreate={handleCreate}
                            onEdit={handleEdit}
                            onRemove={handleRemove}
                            onSettingsClick={handleSettings}
                            onDetailsClick={handleDetailsClick}
                        />
                    </Loader>
                </InfiniteScroll>
            </StyledTable>
            {dialogs}
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    list: state.computeResource.list,
    canGetComputeResources: hasPermission(state, PERMISSION_LIST.GET_COMPUTE_RESOURCES) || hasPermission(state, PERMISSION_LIST.MANAGE_COMPUTE_RESOURCES),
    loadingFlags: {
        isLoadingList: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_LIST),
    },
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ComputeResources);
